import { AppConfig, ChatBVProvider } from '@ltvco/refresh-lib/ctx';
import { Stack, Text, useMediaQuery } from '@ltvco/refresh-lib/theme';
import { RelatedReport } from '@ltvco/refresh-lib/v2';
import CloseIcon from '@mui/icons-material/Close';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { useContext } from 'react';

import { constants } from 'appConstants';
import { ReportColumnContainer } from 'components/ChatBV/components';
import {
  EmailChatBvReport,
  PersonChatBvReport,
  VehicleChatBvReport,
  PropertyChatBVReport,
  PhoneChatBVReport,
  ObituaryChatBvReport,
  ContactChatBvReport,
  AncestorChatBvReport,
} from 'components/ChatBV/components/ChatPage/Report';
import { CHATBV_GA4_EVENTS, SEARCH_TYPES } from 'components/ChatBV/constants';
import { GenericReportResponse } from 'components/ChatBV/types';

type ReportColumnProps = {
  onClose: () => void;
  permalink?: string;
  type?: string;
  currentWidth: string;
  reportData?: GenericReportResponse;
  handleOpenReportInChatBV?: (message: string) => void;
  handleOpenPersonReportInChatBV?: (bvid: string) => void;
  handleRelatedReportClick?: (relatedReport: RelatedReport) => void;
};

export const ReportColumn = ({
  onClose,
  permalink,
  type,
  currentWidth,
  reportData,
  handleOpenReportInChatBV,
  handleOpenPersonReportInChatBV,
  handleRelatedReportClick,
}: ReportColumnProps) => {
  const isMobileBreakpoint = useMediaQuery('(max-width: 1024px)');
  const { trackEventGA4 } = useContext(AppConfig);

  const getReportTitle = () => {
    switch (type) {
      case SEARCH_TYPES.PHONE:
        return 'Phone Report';
      case SEARCH_TYPES.PROPERTY:
        return 'Property Report';
      case SEARCH_TYPES.VEHICLE:
        return 'Vehicle Report';
      case SEARCH_TYPES.EMAIL:
        return 'Email Report';
      case SEARCH_TYPES.PERSON:
        return 'Person Report';
      case SEARCH_TYPES.OBITUARY:
        return 'Obituary Report';
      case SEARCH_TYPES.CONTACT:
        return 'Contact Report';
      case SEARCH_TYPES.ANCESTOR:
        return 'Ancestor Report';
      default:
        return 'Report';
    }
  };

  const handleReportRedirect = () => {
    trackEventGA4({
      event_name: CHATBV_GA4_EVENTS.name,
      type: CHATBV_GA4_EVENTS.report.type,
      subtype: CHATBV_GA4_EVENTS.report.subTypes.open_report_in_new_tab,
    });
    if (type && permalink) {
      const reportUrl = `${constants.links.baseUrl}/report/${type}?permalink=${permalink}`;
      window.open(reportUrl, '_blank');
    }
  };

  return (
    <>
      <ReportColumnContainer
        direction="column"
        spacing={2}
        minWidth={currentWidth}
        width="40%"
        height="98%"
        data-testid="report-column-container"
        sx={{
          ...(isMobileBreakpoint
            ? {
                animation: 'fadeInScale 0.4s ease-out',
                '@keyframes fadeInScale': {
                  '0%': {
                    transform: 'scale(0.95)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'scale(1)',
                    opacity: 1,
                  },
                },
              }
            : {
                animation: 'slideInFromRight 0.5s ease-out',
                '@keyframes slideInFromRight': {
                  '0%': {
                    transform: 'translateX(100%)',
                    opacity: 0,
                  },
                  '100%': {
                    transform: 'translateX(0)',
                    opacity: 1,
                  },
                },
              }),
        }}
      >
        <Stack
          direction="row"
          justifyContent="space-between"
          sx={{
            position: 'sticky',
            top: 0,
            backgroundColor: 'background.paper',
            zIndex: 1,
            padding: '16px 20px',
          }}
        >
          <Text variant="body1" fontWeight="bold" color="text.primary">
            {getReportTitle()}
          </Text>
          <Stack direction="row" spacing={2}>
            <OpenInNewIcon
              sx={{ color: 'text.secondary', cursor: 'pointer' }}
              onClick={handleReportRedirect}
            />
            <CloseIcon
              onClick={onClose}
              sx={{ color: 'text.secondary', cursor: 'pointer' }}
            />
          </Stack>
        </Stack>
        <ChatBVProvider
          width="100%"
          openReportInChatBV={handleOpenReportInChatBV}
          openPersonReportInChatBV={handleOpenPersonReportInChatBV}
          openRelatedReportInChatBV={handleRelatedReportClick}
        >
          {type === SEARCH_TYPES.PHONE && permalink && (
            <PhoneChatBVReport
              permalink={permalink || ''}
              reportData={reportData}
            />
          )}
          {type === SEARCH_TYPES.PROPERTY && permalink && (
            <PropertyChatBVReport
              permalink={permalink}
              reportData={reportData}
            />
          )}
          {type === SEARCH_TYPES.VEHICLE && permalink && (
            <VehicleChatBvReport
              permalink={permalink}
              reportData={reportData}
            />
          )}
          {type === SEARCH_TYPES.EMAIL && permalink && (
            <EmailChatBvReport permalink={permalink} reportData={reportData} />
          )}
          {type === SEARCH_TYPES.PERSON && permalink && (
            <PersonChatBvReport permalink={permalink} />
          )}
          {type === SEARCH_TYPES.OBITUARY && permalink && (
            <ObituaryChatBvReport permalink={permalink} />
          )}
          {type === SEARCH_TYPES.CONTACT && permalink && (
            <ContactChatBvReport permalink={permalink} />
          )}
          {type === SEARCH_TYPES.ANCESTOR && permalink && (
            <AncestorChatBvReport permalink={permalink} />
          )}
        </ChatBVProvider>
      </ReportColumnContainer>
    </>
  );
};
